import type { TaskRunEvent } from "@mlx/contracts";

export type RunSourceKind =
  | "twin"
  | "web"
  | "plugin"
  | "warehouse"
  | "workspace"
  | "memory";

export type RunSourceUsage = {
  key: string;
  kind: RunSourceKind;
  label: string;
  provider?: string;
  pluginSlug?: string;
  callCount: number;
  documentCount: number;
  searchCount: number;
  fetchCount: number;
};

type ToolContext = {
  pluginSlug?: string;
  pluginLabel?: string;
};

const WORKSPACE_SOURCE_TOOL =
  /^twin_workspace_(?:aggregate|extract|get|list|query|read|search)_/;

// The consolidated CLI-tool lane emits tool events without pluginSlug; a
// registry-backed resolver lets provenance still attribute those calls.
export type ResolveToolPlugin = (
  toolName: string,
) => { pluginSlug: string; pluginLabel?: string } | undefined;

export function buildRunSourceUsage(
  events: TaskRunEvent[] | undefined,
  resolveToolPlugin?: ResolveToolPlugin,
): RunSourceUsage[] {
  const contextByCall = new Map<string, ToolContext>();
  for (const event of events ?? []) {
    if (event.kind !== "tool.started" && event.kind !== "tool.updated")
      continue;
    const payload = readRecord(event.payload);
    const toolCallId = readString(payload?.toolCallId);
    if (!toolCallId) continue;
    const existing = contextByCall.get(toolCallId);
    contextByCall.set(toolCallId, {
      pluginSlug: readString(payload?.pluginSlug) ?? existing?.pluginSlug,
      pluginLabel: readString(payload?.pluginLabel) ?? existing?.pluginLabel,
    });
  }

  const usageByKey = new Map<string, RunSourceUsage>();
  for (const event of events ?? []) {
    if (event.kind !== "tool.completed") continue;
    const payload = readRecord(event.payload);
    if (!payload || payload.isError === true) continue;
    const toolName = readString(payload.toolName);
    if (!toolName) continue;
    const context = contextByCall.get(readString(payload.toolCallId) ?? "");
    const source = classifySource({
      toolName,
      provider: readString(payload.provider),
      pluginSlug: readString(payload.pluginSlug) ?? context?.pluginSlug,
      pluginLabel: readString(payload.pluginLabel) ?? context?.pluginLabel,
      resolveToolPlugin,
    });
    if (!source) continue;

    const usage = readRecord(payload.usage);
    const existing = usageByKey.get(source.key);
    if (existing) {
      existing.callCount += 1;
      existing.documentCount += readCount(usage?.documentCount);
      existing.searchCount += readCount(usage?.searchRequestCount);
      existing.fetchCount += readCount(usage?.fetchRequestCount);
      continue;
    }
    usageByKey.set(source.key, {
      ...source,
      callCount: 1,
      documentCount: readCount(usage?.documentCount),
      searchCount: readCount(usage?.searchRequestCount),
      fetchCount: readCount(usage?.fetchRequestCount),
    });
  }

  const sources = [...usageByKey.values()];
  if (sources.length === 0) return [];
  const twinIndex = sources.findIndex((source) => source.kind === "twin");
  if (twinIndex >= 0) {
    if (twinIndex > 0) {
      const [twin] = sources.splice(twinIndex, 1);
      sources.unshift(twin!);
    }
    return sources;
  }
  sources.unshift({
    key: "twin",
    kind: "twin",
    label: "Twin",
    callCount: 0,
    documentCount: 0,
    searchCount: 0,
    fetchCount: 0,
  });
  return sources;
}

export function runUsedTwin(
  events: TaskRunEvent[] | undefined,
  resolveToolPlugin?: ResolveToolPlugin,
): boolean {
  return buildRunSourceUsage(events, resolveToolPlugin).some(
    (source) => source.kind === "twin",
  );
}

export function sourceUsageMetric(source: RunSourceUsage): string {
  if (source.kind === "twin" && source.callCount < 1) return "consulted";
  if (source.kind === "web" && source.documentCount > 0) {
    return `${source.documentCount} source${source.documentCount === 1 ? "" : "s"}`;
  }
  const [singular, plural] =
    source.kind === "warehouse"
      ? ["query", "queries"]
      : source.kind === "workspace" ||
          source.kind === "memory" ||
          source.kind === "twin"
        ? ["lookup", "lookups"]
        : ["call", "calls"];
  return `${source.callCount} ${source.callCount === 1 ? singular : plural}`;
}

export function sourceUsageDescription(source: RunSourceUsage): string {
  if (source.kind === "twin" && source.callCount < 1) {
    return "The Twin was consulted for this retrieval.";
  }
  if (source.kind !== "web") {
    return `${source.label} · ${sourceUsageMetric(source)}`;
  }
  const operations = [
    source.searchCount > 0
      ? `${source.searchCount} search${source.searchCount === 1 ? "" : "es"}`
      : undefined,
    source.fetchCount > 0
      ? `${source.fetchCount} fetch${source.fetchCount === 1 ? "" : "es"}`
      : undefined,
    source.documentCount > 0 ? sourceUsageMetric(source) : undefined,
  ].filter((value): value is string => Boolean(value));
  return [
    source.provider ? `Web via ${humanize(source.provider)}` : "Web",
    ...operations,
  ].join(" · ");
}

function classifySource(input: {
  toolName: string;
  provider?: string;
  pluginSlug?: string;
  pluginLabel?: string;
  resolveToolPlugin?: ResolveToolPlugin;
}): Omit<
  RunSourceUsage,
  "callCount" | "documentCount" | "searchCount" | "fetchCount"
> | null {
  if (
    input.toolName === "twin_governed_product_query" ||
    input.toolName === "twin_plan_and_retrieve_governed_evidence"
  ) {
    return { key: "twin", kind: "twin", label: "Twin" };
  }
  if (
    input.toolName === "twin_web_search" ||
    input.toolName === "twin_google_grounded_answer" ||
    input.toolName === "twin_web_fetch" ||
    input.toolName === "mlx_web_search" ||
    input.toolName === "mlx_web_fetch"
  ) {
    const provider = input.provider?.toLowerCase();
    return {
      key: `web:${provider ?? "default"}`,
      kind: "web",
      label: "Web",
      provider,
    };
  }
  if (input.pluginSlug) {
    return {
      key: `plugin:${input.pluginSlug}`,
      kind: "plugin",
      label: input.pluginLabel ?? humanize(input.pluginSlug),
      pluginSlug: input.pluginSlug,
    };
  }
  if (input.toolName.startsWith("warehouse_")) {
    return { key: "warehouse", kind: "warehouse", label: "Warehouse" };
  }
  if (WORKSPACE_SOURCE_TOOL.test(input.toolName)) {
    return { key: "workspace", kind: "workspace", label: "Workspace" };
  }
  if (
    input.toolName === "load_memory_context" ||
    input.toolName === "search_episodic_memory"
  ) {
    return { key: "memory", kind: "memory", label: "Memory" };
  }
  // Last resort: registry resolution by tool name, so CLI-lane tool events
  // without a payload pluginSlug still attribute to their connector. Every
  // name-based rule above keeps precedence.
  const resolved = input.resolveToolPlugin?.(input.toolName);
  if (resolved) {
    return {
      key: `plugin:${resolved.pluginSlug}`,
      kind: "plugin",
      label: resolved.pluginLabel ?? humanize(resolved.pluginSlug),
      pluginSlug: resolved.pluginSlug,
    };
  }
  return null;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readCount(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : 0;
}

function humanize(value: string): string {
  const normalized = value.replace(/[-_]+/g, " ").trim();
  return normalized
    ? normalized.replace(/\b\w/g, (character) => character.toUpperCase())
    : value;
}
